<script setup>
import UiActionRowOperator from "@/routes/DataDeletion/components/UIActionRow/UiActionRowOperator.vue";
import UiActionRow from "@/routes/DataDeletion/components/UIActionRow/UiActionRow.vue";

const props = defineProps({
  operatorEnabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  actionRequiredFamilies: {
    type: Array,
    required: true,
  },
  formattedDate: {
    type: String,
    required: true,
  },
  actionRequiredBrokersGroupedByFamily: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  "openOperatorModal",
  "manualDone",
  "toggleOpen",
  "updateActionRequiredFamilies",
]);

function handleOpenOperatorModal(brokerFamily) {
  emit("openOperatorModal", brokerFamily);
}

function handleManualDone(brokerFamily) {
  emit("manualDone", brokerFamily);
}

function handleToggleOpen(brokerFamily) {
  emit("toggleOpen", brokerFamily);
}

function handleUpdateActionRequiredFamilies(family) {
  // Pass the event directly up
  emit("updateActionRequiredFamilies", family);
}
</script>

<template>
  <div class="action-rows-container">
    <template v-if="operatorEnabled">
      <UiActionRowOperator
        v-for="(brokerFamily, idx) in props.actionRequiredFamilies"
        :key="`operator-${brokerFamily?.name}-${idx}`"
        :family="brokerFamily"
        :date="props.formattedDate"
        :brokers="
          props.actionRequiredBrokersGroupedByFamily?.[brokerFamily?.name]
        "
        @open-operator-modal="handleOpenOperatorModal(brokerFamily)"
        @toggleOpen="handleToggleOpen(brokerFamily)"
        @updateActionRequiredFamilies="handleUpdateActionRequiredFamilies"
      />
    </template>
    <template v-else>
      <UiActionRow
        v-for="(brokerFamily, idx) in props.actionRequiredFamilies"
        :key="`${brokerFamily?.name}-${idx}`"
        :name="brokerFamily?.name"
        :count="brokerFamily?.brokers?.length || 0"
        :opened="!!brokerFamily?.opened"
        :brokers="brokerFamily?.brokers"
        :instructions="brokerFamily?.removal_steps"
        :date="props.formattedDate"
        @done="handleManualDone(brokerFamily)"
        @toggleOpen="handleToggleOpen(brokerFamily)"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.action-rows-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.action-rows-container :deep(.action-row-wrapper) {
  padding: 24px;
  border-radius: 12px;
  border: 1px solid $color-primary-10;
  background-color: $color-primary-1;

  @at-root .theme-dark & {
    background-color: $color-primary-5;
  }

  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border: 1px solid $color-primary-100;
    transform: scale(1.005);
    transition: all 0.3s ease;
  }

  .action-row {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;

    .action-row-item {
      font-size: 15px;
      color: $color-primary-100;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      width: calc((100% + 50px) / 3);
      text-wrap: wrap;

      &:first-of-type {
        width: calc(((100% + 50px) / 3) - 24px);
      }

      &:last-of-type {
        width: calc(((100% + 50px) / 3) - 24px);
      }

      &.name {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 16px;
        flex-wrap: nowrap;
        overflow: auto;
        white-space: wrap;

        .icon {
          width: 11px;
        }

        .text {
          @include line-clamp(2);
        }
      }

      &.review-text {
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        svg {
          height: 16px;
          width: auto;
        }
      }
    }

    .done-button {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      width: 95%;
      position: relative;

      .short-button {
        position: absolute;
        display: none;
      }

      .med-button {
        position: absolute;
        display: none;
      }

      .long-button {
        position: absolute;
        display: flex;
      }

      @media (max-width: 530px) {
        .short-button {
          display: flex;
        }

        .med-button {
          display: none;
        }

        .long-button {
          display: none;
        }
      }

      @media (min-width: 530px) and (max-width: 840px) {
        .short-button {
          display: none;
        }

        .med-button {
          display: flex;
        }

        .long-button {
          display: none;
        }
      }

      @media (min-width: 840px) {
        .short-button {
          display: none;
        }

        .med-button {
          display: none;
        }

        .long-button {
          display: flex;
        }
      }
    }
  }

  &.opened {
    border: 1px solid $color-primary-100;
    background-color: transparent;
    box-shadow: 0px 18px 34px 0px rgba($color-primary-100-light, 0.15);
    transition: all 0.3s ease-in-out;

    @at-root .theme-dark & {
      box-shadow: 0px 18px 34px 0px rgba($color-primary-50-dark, 0.15);
    }
  }

  .action-row-details-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    opacity: 0;
    transition:
      grid-template-rows 0.3s ease-out,
      opacity 0.3s ease-out;

    &.opened {
      grid-template-rows: 1fr;
      opacity: 1;
    }

    .action-row-details {
      justify-content: center;
      align-items: flex-start;
      flex-direction: column;
      color: $color-primary-100;
      display: flex;
      overflow: hidden;
    }
  }

  .link-text {
    color: $color-primary-100;
    cursor: pointer;
    text-decoration: underline;
    &:hover {
      opacity: 0.8;
      transition: all 0.3s ease-in-out;
    }
  }
}
</style>
